import { createFileRoute, Outlet, useNavigate } from "@tanstack/react-router"
import { useAuth } from "hooks/use-auth"
import { useUserType } from "hooks/use-user-type"
import { useEffect } from "react"
import { PaymentRequiredModal } from "@/components/billing/payment-required-modal"
import { PageLoader } from "@/components/page-loader"
import { trpc } from "@/lib/trpc"

export const Route = createFileRoute("/_authed/_professional")({
  component: ProfessionalLayout,
})

function ProfessionalLayout() {
  const { isAuthenticated, isLoading } = useAuth()
  const { isProfessional } = useUserType()
  const navigate = useNavigate()

  const isPaywallDebug = window?.location?.search?.includes("debugPaywall=1") ?? false

  const {
    data: paymentStatus,
    isLoading: isCheckingPayment,
    error: paymentStatusError,
    status: paymentStatusQueryStatus,
    isFetching: paymentStatusIsFetching,
    refetch: refetchPaymentStatus,
  } = trpc.stripe.checkPaymentMethod.useQuery(undefined, {
    enabled: isAuthenticated && isProfessional,
    retry: 0,
  })

  useEffect(() => {
    if (!isPaywallDebug) return
    // One clean line you can grep in console
    // biome-ignore lint/suspicious/noConsole: stripe debug logging
    console.info("[paywall] layout", {
      isLoading,
      isAuthenticated,
      isProfessional,
      paymentStatusQueryStatus,
      paymentStatusIsFetching,
      paymentStatus,
      paymentStatusError: paymentStatusError
        ? {
            message: paymentStatusError.message,
            code: paymentStatusError.data?.code,
            httpStatus: paymentStatusError.data?.httpStatus,
            path: paymentStatusError.data?.path,
          }
        : null,
    })
  }, [
    isPaywallDebug,
    isLoading,
    isAuthenticated,
    isProfessional,
    paymentStatusQueryStatus,
    paymentStatusIsFetching,
    paymentStatus,
    paymentStatusError,
  ])

  // Optional: if you come back from Stripe with checkout=success, force a refetch
  useEffect(() => {
    if (!isPaywallDebug) return
    const checkout = new URLSearchParams(window.location.search).get("checkout")
    if (checkout === "success") {
      // biome-ignore lint/suspicious/noConsole: stripe debug logging
      console.info("[paywall] checkout=success detected, refetching payment status…")
      refetchPaymentStatus()
    }
  }, [isPaywallDebug, refetchPaymentStatus])

  useEffect(() => {
    if (!isLoading && isAuthenticated && !isProfessional) {
      navigate({ to: "/personal/dashboard", replace: true })
    }
  }, [isAuthenticated, isLoading, isProfessional, navigate])

  if (isLoading || isCheckingPayment) return <PageLoader />
  if (!isAuthenticated || !isProfessional) return null

  // IMPORTANT: decide what “no paymentStatus” should mean.
  // If backend returns null/undefined, treat as "no valid payment method" and show modal.
  const mustShowPaywall = !paymentStatus || paymentStatus.hasValidPaymentMethod === false

  return (
    <>
      {mustShowPaywall && <PaymentRequiredModal />}
      <Outlet />
    </>
  )
}
